import { useEffect, useRef, useState } from "react";
import { api, API, fmtMoney } from "@/lib/api";
import { PageHeader, PageBody } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Upload, Trash2, AlertTriangle, CheckCircle2, FileSpreadsheet, Search, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function GSTRecon() {
    const [period, setPeriod] = useState(() => {
        const t = new Date(); const y = t.getFullYear(); const m = t.getMonth() + 1;
        return `${y}-${String(m).padStart(2, "0")}`;
    });
    const [jobs, setJobs] = useState([]);
    const [activeJob, setActiveJob] = useState(null);
    const [match, setMatch] = useState(null);
    const [loading, setLoading] = useState(false);
    const [tab, setTab] = useState("matched");
    const inputRef = useRef(null);

    const loadJobs = async () => {
        const { data } = await api.get("/gst/2b/jobs");
        setJobs(data);
    };
    useEffect(() => { loadJobs(); }, []);

    const onUpload = async (e) => {
        const file = e.target.files?.[0]; if (!file) return;
        const fd = new FormData();
        fd.append("period", period); fd.append("file", file);
        const token = localStorage.getItem("vyastra_token");
        try {
            const res = await fetch(`${API}/gst/2b/upload`, { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || "Upload failed");
            }
            const data = await res.json();
            toast.success(`Uploaded ${data.row_count} portal rows`);
            await loadJobs();
            await runMatch(data.id);
        } catch (e) { toast.error(e.message); }
        finally { if (inputRef.current) inputRef.current.value = ""; }
    };

    const runMatch = async (jid) => {
        setLoading(true);
        try {
            const { data } = await api.post(`/gst/2b/match/${jid}`, {});
            setMatch(data);
            setActiveJob(jid);
        } catch (e) { toast.error(e?.response?.data?.detail || "Match failed"); }
        finally { setLoading(false); }
    };

    const removeJob = async (jid) => {
        if (!confirm("Delete this 2B upload?")) return;
        await api.delete(`/gst/2b/jobs/${jid}`);
        if (activeJob === jid) { setActiveJob(null); setMatch(null); }
        await loadJobs();
    };

    return (
        <>
            <PageHeader
                kicker="TAXES • GSTR 2A/2B"
                title="GST Reconciliation"
                subtitle="Upload your GSTR-2B/2A from the portal — Vyaahar matches every line against your purchase ledger and flags missing, extra, or value-mismatched invoices."
            />
            <PageBody>
                <div className="bg-card border border-border rounded-md p-4 mb-4 flex flex-wrap items-end gap-3" data-testid="gstrecon-toolbar">
                    <div>
                        <Label className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-semibold">Return period (YYYY-MM)</Label>
                        <Input value={period} onChange={(e) => setPeriod(e.target.value)} className="mt-1 w-[140px] h-9" placeholder="2025-12" data-testid="gstrecon-period" />
                    </div>
                    <Button size="sm" onClick={() => inputRef.current?.click()} data-testid="gstrecon-upload">
                        <Upload className="w-3.5 h-3.5 mr-1.5" />Upload 2B / 2A CSV
                    </Button>
                    <input ref={inputRef} type="file" className="hidden" accept=".csv" onChange={onUpload} />
                    <span className="text-[11px] text-muted-foreground ml-2">Expected columns: Supplier GSTIN, Invoice Number, Invoice Date, Invoice Value, Taxable Value, IGST, CGST, SGST</span>
                </div>

                {jobs.length > 0 ? (
                    <div className="bg-card border border-border rounded-md mb-4" data-testid="gstrecon-jobs">
                        <div className="px-4 py-3 border-b border-border text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-semibold">Recent uploads</div>
                        <table className="fin-table">
                            <thead><tr><th>Period</th><th>Filename</th><th className="num">Rows</th><th>Uploaded</th><th></th></tr></thead>
                            <tbody>
                                {jobs.map((j) => (
                                    <tr key={j.id} className={`${activeJob === j.id ? "bg-muted/40" : ""}`} data-testid={`gstrecon-job-${j.id}`}>
                                        <td className="font-medium">{j.period}</td>
                                        <td className="text-[12px] text-muted-foreground">{j.filename}</td>
                                        <td className="num">{j.row_count}</td>
                                        <td className="text-[12px] text-muted-foreground">{j.uploaded_at?.slice(0, 10)} · {j.uploaded_by_name}</td>
                                        <td className="text-right">
                                            <Button variant="ghost" size="sm" className="h-7" onClick={() => runMatch(j.id)} data-testid={`gstrecon-rematch-${j.id}`}><RefreshCw className="w-3.5 h-3.5 mr-1" />Match</Button>
                                            <Button variant="ghost" size="sm" className="h-7 text-destructive" onClick={() => removeJob(j.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="bg-card border border-border rounded-md mb-4 p-6 text-center text-[12.5px] text-muted-foreground" data-testid="gstrecon-jobs">
                        No GSTR-2B uploads yet. Upload a CSV from the GST portal above to get started.
                    </div>
                )}

                {loading && <div className="text-center text-muted-foreground py-8">Matching…</div>}

                {match && !loading && (
                    <>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                            <KpiCard label="Matched" value={match.summary.matched_count} sub={fmtMoney(match.summary.matched_value)} tone="positive" />
                            <KpiCard label="Value Mismatch" value={match.summary.value_mismatch_count} tone={match.summary.value_mismatch_count ? "destructive" : "default"} />
                            <KpiCard label="In 2B, missing in books" value={match.summary.unmatched_in_2b_count} tone={match.summary.unmatched_in_2b_count ? "destructive" : "default"} />
                            <KpiCard label="In books, missing in 2B" value={match.summary.extra_in_books_count} tone={match.summary.extra_in_books_count ? "muted" : "default"} />
                        </div>
                        <Tabs value={tab} onValueChange={setTab}>
                            <TabsList>
                                <TabsTrigger value="matched" data-testid="gstrecon-tab-matched">Matched ({match.summary.matched_count})</TabsTrigger>
                                <TabsTrigger value="mismatch" data-testid="gstrecon-tab-mismatch">Value mismatch ({match.summary.value_mismatch_count})</TabsTrigger>
                                <TabsTrigger value="missing-books" data-testid="gstrecon-tab-missing-books">Missing in books ({match.summary.unmatched_in_2b_count})</TabsTrigger>
                                <TabsTrigger value="extra-books" data-testid="gstrecon-tab-extra-books">Missing in 2B ({match.summary.extra_in_books_count})</TabsTrigger>
                            </TabsList>
                            <TabsContent value="matched">
                                <Tbl rows={match.matched.map((m) => ({
                                    icon: <CheckCircle2 className="w-3.5 h-3.5 text-positive" />,
                                    cols: [m.portal.supplier_gstin, m.portal.supplier_name, m.bill.number, m.bill.date, fmtMoney(m.bill.total)],
                                }))} headers={["", "Supplier GSTIN", "Supplier name", "Invoice #", "Date", "Amount"]} />
                            </TabsContent>
                            <TabsContent value="mismatch">
                                <Tbl rows={match.value_mismatch.map((m) => ({
                                    icon: <AlertTriangle className="w-3.5 h-3.5 text-destructive" />,
                                    cols: [m.portal.supplier_name, m.bill.number, fmtMoney(m.portal.invoice_value), fmtMoney(m.bill.total), <span className="text-destructive font-medium">{m.diff > 0 ? "+" : ""}{fmtMoney(m.diff)}</span>],
                                }))} headers={["", "Supplier name", "Invoice #", "Portal value", "Books value", "Diff"]} />
                            </TabsContent>
                            <TabsContent value="missing-books">
                                <Tbl rows={match.unmatched_in_2b.map((r) => ({
                                    icon: <AlertTriangle className="w-3.5 h-3.5 text-destructive" />,
                                    cols: [r.supplier_gstin, r.supplier_name, r.invoice_no, r.invoice_date, fmtMoney(r.invoice_value)],
                                }))} headers={["", "Supplier GSTIN", "Supplier name", "Invoice #", "Date", "Amount"]} />
                            </TabsContent>
                            <TabsContent value="extra-books">
                                <Tbl rows={match.extra_in_books.map((b) => ({
                                    icon: <Search className="w-3.5 h-3.5 text-amber-700" />,
                                    cols: [b.vendor_gstin, b.vendor_name, b.number, b.date, fmtMoney(b.total)],
                                }))} headers={["", "Vendor GSTIN", "Vendor name", "Bill #", "Date", "Amount"]} />
                            </TabsContent>
                        </Tabs>
                    </>
                )}
            </PageBody>
        </>
    );
}

function KpiCard({ label, value, sub, tone }) {
    const toneCls = tone === "positive" ? "text-positive" : tone === "destructive" ? "text-destructive" : tone === "muted" ? "text-amber-700" : "";
    return (
        <div className="bg-card border border-border rounded-md p-4">
            <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-semibold">{label}</div>
            <div className={`font-display text-2xl font-light tracking-tight mt-1 ${toneCls}`}>{value}</div>
            {sub && <div className="text-[11px] text-muted-foreground font-mono mt-0.5">{sub}</div>}
        </div>
    );
}

function Tbl({ headers, rows }) {
    if (!rows || rows.length === 0) return <div className="bg-card border border-border rounded-md p-8 text-center text-muted-foreground text-sm mt-3">Nothing here.</div>;
    return (
        <div className="bg-card border border-border rounded-md overflow-hidden mt-3 max-h-[60vh] overflow-y-auto">
            <table className="fin-table">
                <thead className="sticky top-0 bg-muted/40">
                    <tr>{headers.map((h, i) => <th key={i} className={i >= headers.length - 1 ? "num" : ""}>{h}</th>)}</tr>
                </thead>
                <tbody>
                    {rows.map((r, i) => (
                        <tr key={i}>
                            <td className="w-6">{r.icon}</td>
                            {r.cols.map((c, j) => <td key={j} className={j === r.cols.length - 1 ? "num font-mono" : (j === r.cols.length - 2 ? "font-mono text-[12px]" : "")}>{c}</td>)}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
